import { cn } from '@/lib/utils'
import { DadoCubo } from './DadoCubo'
import type { DiceType } from '@/types/dice'

/**
 * O resultado de uma jogada — a fonte única de como uma rolagem se lê.
 *
 * Existe por causa de um padrão que mordeu **três vezes** no módulo de dados: a
 * jogada era desenhada em telas diferentes (bandeja, cerimônia, balão do chat),
 * cada uma atualizada numa data, e uma sempre ficava para trás — a cerimônia
 * que nunca aprendeu a bandeja, o balão que ignorava os chips de atributo.
 *
 * Depois que o `DadoCubo` virou a fonte única do dado, este componente é a
 * fonte única da JOGADA. Bandeja e cerimônia montam a mesma coisa; a diferença
 * entre elas passa a ser só a moldura e quem pode enviar.
 *
 * A anatomia, sempre nesta ordem:
 *   1. os dados, cada um com o número que tirou (descartados apagados)
 *   2. a conta por extenso — o que permite conferir, e não só receber
 *   3. o total
 *   4. o veredito, quando houve dificuldade
 */

/**
 * O ritmo da jogada, em milissegundos.
 *
 * Existe como constante compartilhada porque a bandeja e a cerimônia precisam
 * do MESMO tempo: são a mesma jogada vista de dois lugares.
 *
 * O chacoalho **não** pode durar o que a requisição dura — o servidor responde
 * em ~100ms, e foi assim que a primeira versão saiu: o jogador tocava e o dado
 * já estava parado ("rápido demais, nem deu para ver os dados girarem"). O
 * tempo aqui é de LEITURA humana, não de rede: sacode, assenta, resolve.
 */
export const RITMO = {
  /** Sacudindo os dados. Mínimo, mesmo que a resposta chegue antes. */
  chacoalho: 1200,
  /** O cubo girando até a face sorteada (igual à transição do DadoCubo). */
  assentar: 1150,
  /** Depois de assentar, a regra é aplicada à vista (descartados apagam). */
  resolver: 700,
}

export interface JogadaExibida {
  spec: { type: string; result: number }[]
  kept?: number[]
  rule?: string | null
  expression?: string | null
  modifiers?: { id: string; name: string; value: number }[]
  attributeName?: string
  attributeValue?: number
  bonus?: number
  result?: number
  total?: number
  difficulty?: number
  outcome?: 'success' | 'failure'
  /**
   * Vantagem / desvantagem (fase 2 da rolagem clássica).
   *
   * Os dois dados vêm no `spec` e o `kept` diz qual valeu — o mesmo vocabulário
   * que a bandeja já usa para dado descartado. Este campo é só o RÓTULO: sem
   * ele, dois d20 com um apagado ficariam sem explicação na tela.
   */
  advantage?: 'vantagem' | 'desvantagem'
  /**
   * Crítico natural (fase 3 da rolagem clássica): `'acerto'` no 20, `'erro'`
   * no 1 — só no teste com d20.
   *
   * Muda o veredito (o dado decide sozinho, independente do total) e o brilho
   * do dado que valeu. Vem do SERVIDOR: derivar aqui daria duas verdades sobre
   * a mesma jogada, e a do cliente estaria sempre a um `if` de divergir.
   */
  critical?: 'acerto' | 'erro'
  /**
   * O que estava sendo TENTADO, nomeado pelo mestre ao pedir o teste.
   *
   * Continua na tela DEPOIS de o dado assentar, de propósito: é no resultado
   * que a mesa precisa lembrar do que estava em jogo. Some junto com a jogada.
   */
  actionName?: string
}

interface ResultadoDaJogadaProps {
  jogada: JogadaExibida
  /** Os dados ainda estão no ar: mostra os cubos chacoalhando, sem números. */
  rolando?: boolean
  /** A regra já foi aplicada à vista (descartados apagam, conta aparece). */
  resolvido?: boolean
  /** Aresta do cubo. A cerimônia usa maior que a bandeja. */
  tamanhoDoDado?: number
  /** Tema escuro (cerimônia sobre fundo preto) x claro (bandeja). */
  variante?: 'clara' | 'escura'
  className?: string
}

export function ResultadoDaJogada({
  jogada,
  rolando = false,
  resolvido = true,
  tamanhoDoDado = 52,
  variante = 'clara',
  className,
}: ResultadoDaJogadaProps) {
  const escura = variante === 'escura'
  const mantidos = new Set(jogada.kept ?? jogada.spec.map((_, i) => i))
  const contouSucessos = jogada.rule === 'sucessos'
  const temResultado = !rolando && jogada.result !== undefined

  const parcelas = [
    ...(jogada.modifiers ?? []).map(m => ({ nome: m.name, valor: m.value })),
    ...(jogada.attributeName && jogada.attributeValue !== undefined
      ? [{ nome: jogada.attributeName, valor: jogada.attributeValue }]
      : []),
    ...(jogada.bonus ? [{ nome: 'bônus', valor: jogada.bonus }] : []),
  ]

  const somaDosDados = jogada.spec
    .filter((_, i) => mantidos.has(i))
    .reduce((soma, d) => soma + d.result, 0)

  return (
    <div className={cn('flex flex-col items-center gap-3', className)}>
      {/* 0a. o que está sendo tentado: primeiro, porque é o que dá sentido a
              tudo que vem abaixo. */}
      {jogada.actionName && (
        <p
          data-testid="jogada-acao"
          className="text-center text-base font-semibold text-game-primary dark:text-white"
        >
          {jogada.actionName}
        </p>
      )}

      {/* 0. o rótulo da vantagem, quando houver — vem ANTES dos dados porque
             explica por que há dois deles na tela. */}
      {jogada.advantage && (
        <span
          data-testid="jogada-vantagem"
          className={cn(
            'px-2.5 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wide',
            jogada.advantage === 'vantagem'
              ? 'bg-emerald-500/20 text-emerald-300'
              : 'bg-rose-500/20 text-rose-300'
          )}
        >
          {`com ${jogada.advantage}`}
        </span>
      )}

      {/* 1. os dados */}
      <div
        className="flex flex-wrap justify-center gap-2"
        data-testid="jogada-dados"
      >
        {jogada.spec.map((dado, i) => {
          const foraDaConta = temResultado && resolvido && !mantidos.has(i)
          // O brilho é do dado que VALEU: num crítico com vantagem, o 20 do
          // dado descartado não é crítico nenhum (regra clássica).
          const eOCritico = !!jogada.critical && temResultado && !foraDaConta
          return (
            <div
              key={i}
              data-testid={foraDaConta ? 'jogada-dado-fora' : 'jogada-dado'}
              className="flex flex-col items-center gap-1"
            >
              <div
                className={cn(
                  'rounded-xl transition-shadow duration-500',
                  eOCritico && jogada.critical === 'acerto' &&
                    'ring-2 ring-amber-300 shadow-[0_0_18px_4px_rgba(252,211,77,0.55)]',
                  eOCritico && jogada.critical === 'erro' &&
                    'ring-2 ring-rose-400 shadow-[0_0_18px_4px_rgba(244,63,94,0.5)]'
                )}
                data-testid={eOCritico ? 'jogada-dado-critico' : undefined}
              >
                <DadoCubo
                  tipo={dado.type as DiceType}
                  resultado={temResultado ? dado.result : undefined}
                  chacoalhando={rolando}
                  fora={foraDaConta}
                  tamanho={tamanhoDoDado}
                />
              </div>
              <span className={cn(
                'font-mono text-[10px]',
                escura ? 'text-white/60' : 'text-game-muted',
                foraDaConta && 'line-through'
              )}>
                {dado.type.toLowerCase()}
              </span>
            </div>
          )
        })}
      </div>

      {temResultado && (
        <div className={cn(
          'flex flex-col items-center gap-1 transition-opacity duration-500',
          resolvido ? 'opacity-100' : 'opacity-0'
        )}>
          {/* 2. a conta por extenso */}
          <span
            className={cn(
              'font-mono text-sm text-center',
              escura ? 'text-white/80' : 'text-game-secondary'
            )}
            data-testid="jogada-conta"
          >
            {jogada.spec.filter((_, i) => mantidos.has(i)).map(d => d.result).join(' + ')}
            {parcelas.map(p => (
              <span key={p.nome}>
                {` ${p.valor >= 0 ? '+' : '−'} ${Math.abs(p.valor)} (${p.nome})`}
              </span>
            ))}
            {contouSucessos ? '' : ` = ${jogada.total ?? somaDosDados}`}
          </span>

          {/* 3. o total */}
          <span
            className={cn(
              'text-4xl font-bold tabular-nums leading-none',
              escura ? 'text-white' : 'text-game-primary dark:text-zinc-100'
            )}
            data-testid="jogada-total"
            aria-live="polite"
          >
            {contouSucessos ? jogada.result : (jogada.total ?? somaDosDados)}
          </span>
          <span className={cn(
            'text-xs uppercase tracking-wide',
            escura ? 'text-white/60' : 'text-game-muted'
          )}>
            {contouSucessos
              ? (jogada.result === 1 ? 'sucesso' : 'sucessos')
              : jogada.rule === 'maiores'
                ? `manteve ${jogada.kept?.length ?? 0}`
                : 'total'}
          </span>

          {/* 4. o veredito — e, no crítico, o que o atropelou.
                 O crítico aparece MESMO sem dificuldade: sem veredito a
                 rolagem não tem alvo, mas 20 natural continua sendo um
                 momento de mesa. */}
          {jogada.critical ? (
            <span
              data-testid="jogada-critico"
              className={cn(
                'mt-1 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide',
                jogada.critical === 'acerto'
                  ? 'bg-amber-400/25 text-amber-200'
                  : 'bg-rose-500/25 text-rose-200'
              )}
            >
              {jogada.critical === 'acerto'
                ? 'acerto crítico — 20 natural'
                : 'falha crítica — 1 natural'}
            </span>
          ) : jogada.difficulty !== undefined && jogada.outcome ? (
            <span
              data-testid="jogada-veredito"
              className={cn(
                'mt-1 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide',
                jogada.outcome === 'success'
                  ? 'bg-emerald-500/20 text-emerald-300'
                  : 'bg-rose-500/20 text-rose-300'
              )}
            >
              {`vs ${jogada.difficulty} — ${jogada.outcome === 'success' ? 'sucesso' : 'falha'}`}
            </span>
          ) : null}
        </div>
      )}
    </div>
  )
}

export default ResultadoDaJogada
